import MessageEditorControlled from '@common-components/message-editor/message-editor-controlled';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { LinkPreview } from '@bt-common/schemas/bt/user-message';
import { syncDraftToTemplate, updateCurrentTemplateDraft } from '@common/reducers/CampaignReducer';
import {
    setTemplateId,
    setTemplateName,
    resetScheduledMessageEditor,
    loadTemplateData,
} from '@common/reducers/ScheduledMessageEditorReducer';
import { Asset } from '@common/types/interface';
import { debounce } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Poll } from '../../add-scheduled-message-button/schedule-message-types';

export type BulkTemplateEditorState = {
    message: string;
    isPoll: boolean;
    poll: Partial<Poll>;
    asset: Asset;
    imageId: string;
    sendLinkPreview: boolean;
    linkPreview: LinkPreview;
    name: string; // Template name
};

type BulkMessageEditorWrapperProps = { // Other props
    isPreview?: boolean;
}

export default function BulkMessageEditorWrapper(props: BulkMessageEditorWrapperProps) {
    const dispatch = useAppDispatch();
    
    // Get current state from both reducers
    const currentTemplate = useAppSelector((state: any) => state.CampaignReducer.currentTemplate);
    const editorState = useAppSelector((state: any) => state.ScheduledMessageEditorReducer);
    const activeTab = useAppSelector((state: any) => state.CampaignReducer.activeTab);

    // Load template data when template changes
    useEffect(() => {
        if (currentTemplate) {
            dispatch(loadTemplateData(currentTemplate));
        } else {
            dispatch(resetScheduledMessageEditor());
        }
    }, [dispatch, currentTemplate?._id]); // Only reset when template ID changes

    // CRITICAL: Set templateId for edit mode (save buttons depend on this)
    useEffect(() => {
        if (currentTemplate?._id !== undefined) {
            dispatch(setTemplateId(currentTemplate._id || ''));
        }
    }, [dispatch, currentTemplate?._id]);

    // CRITICAL: Set templateName for template header (template header depends on this)
    useEffect(() => {
        if (currentTemplate?.name !== undefined) {
            dispatch(setTemplateName(currentTemplate.name || ''));
        }
    }, [dispatch, currentTemplate?.name]);

    // Debounced callback to sync back to campaign draft (only for campaign context)
    const debouncedSyncToDraft = useMemo(
        () => debounce((state: BulkTemplateEditorState) => {
            // Only sync to draft if we're in campaign context (messageAndFiles tab)
            if (activeTab === 'messageAndFiles') {
                dispatch(updateCurrentTemplateDraft({
                    message: state.message,
                    isPoll: state.isPoll,
                    poll: state.poll,
                    asset: state.asset,
                    imageId: state.imageId,
                    sendLinkPreview: state.sendLinkPreview,
                    linkPreview: state.linkPreview,
                    name: state.name,
                }));
            }
        }, 200),
        [dispatch, activeTab]
    );

    // Listen to editor state changes and sync to campaign draft (only for campaign context)
    useEffect(() => {
        if (activeTab === 'messageAndFiles') {
            const stateToSync: BulkTemplateEditorState = {
                message: editorState.message,
                isPoll: editorState.isPoll,
                poll: editorState.poll,
                asset: editorState.asset,
                imageId: editorState.imageId,
                sendLinkPreview: editorState.sendLinkPreview,
                linkPreview: editorState.linkPreview,
                name: editorState.templateName, // Use templateName from editor state
            };

            debouncedSyncToDraft(stateToSync);
        }
    }, [
        editorState.message,
        editorState.isPoll,
        editorState.poll,
        editorState.asset,
        editorState.imageId,
        editorState.sendLinkPreview,
        editorState.linkPreview,
        editorState.templateName, // Listen to templateName changes
        debouncedSyncToDraft,
        activeTab,
    ]);

    // Sync draft back to main template when navigating away from messageAndFiles tab (only for campaign context)
    useEffect(() => {
        return () => {
            // Only sync when leaving campaign context
            if (activeTab === 'messageAndFiles') {
                dispatch(syncDraftToTemplate());
            }
        };
    }, [dispatch, activeTab]);

    // Also sync when tab changes away from messageAndFiles (only for campaign context)
    useEffect(() => {
        if (activeTab !== 'messageAndFiles' && activeTab) {
            dispatch(syncDraftToTemplate());
        }
    }, [activeTab, dispatch]);

    // Render the pure component with minimal props
    return (
        <MessageEditorControlled
            isPreview={props.isPreview}
            editorContext="scheduleCampaign"
        />
    );
} 